import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { sources } from '@App/components/Map/map.sources';

import { getFillColorExpression, layers } from './consumers.layers';
import {
  legendModes,
  setHeatReceivedLegendMode,
} from './consumers.store';

import './consumers.css';

const s3BucketUrl = import.meta.env.VITE_S3_BUCKET_URL;

function Consumers({ map, navigate }) {
  const dispatch = useDispatch();
  const legendMode = useSelector(state => state.consumers.legendMode);
  const heatReceivedMaximums = useSelector(state => state.consumers.heatReceivedMaximum);

  useEffect(() => {
    if (!map || !s3BucketUrl) {
      return undefined;
    }

    const createPmtilesSource = ({ type, pmtilesFile, promoteId }) => ({
      type,
      url: `pmtiles://${s3BucketUrl}/${pmtilesFile}`,
      ...(promoteId ? { promoteId } : {}),
    });

    Object.values(sources).forEach((source) => {
      if (!map.getSource(source.id)) {
        map.addSource(source.id, createPmtilesSource(source));
      }
    });

    const styleLayers = map.getStyle().layers;
    const roadLabelsIndex = styleLayers.findIndex(layer => layer.id === 'Road labels');
    const layerAfterRoadLabelsId = roadLabelsIndex >= 0
      ? styleLayers[roadLabelsIndex + 1]?.id
      : undefined;

    Object.values(layers).forEach((layer) => {
      if (!map.getLayer(layer.id)) {
        map.addLayer(layer, layerAfterRoadLabelsId);
      }
    });

    let hoveredGemeenteId;
    let hoveredStatSectorId;
    let lastHeatReceivedMode;

    const updateHeatReceivedLegendMode = () => {
      const heatReceivedMode = map.getZoom() >= 10
        ? legendModes.HEAT_RECEIVED_STAT_SECTOR
        : legendModes.HEAT_RECEIVED_MUNICIPALITY;

      if (heatReceivedMode !== lastHeatReceivedMode) {
        lastHeatReceivedMode = heatReceivedMode;
        dispatch(setHeatReceivedLegendMode(heatReceivedMode));
      }
    };

    const setFeatureHoverState = (source, sourceLayer, featureId, hover) => {
      if (featureId !== undefined && featureId !== null) {
        map.setFeatureState({ source, sourceLayer, id: featureId }, { hover });
      }
    };

    const handleGemeenteClick = (event) => {
      const gemeente = event.features?.[0];

      const gemeenteId = gemeente?.properties?.ID_gemeente;
      if (gemeenteId !== undefined && gemeenteId !== null) {
        navigate(`/afnemers/${encodeURIComponent(String(gemeenteId))}`);
      }
    };

    const handleStatSectorClick = (event) => {
      const sector = event.features?.[0];
      const municipalityId = sector?.properties?.ID_gemeente;
      const statisticalSectorId = sector?.properties?.ID_stat_sect;

      if (
        municipalityId !== undefined
        && municipalityId !== null
        && statisticalSectorId !== undefined
        && statisticalSectorId !== null
      ) {
        navigate(`/afnemers/${encodeURIComponent(String(municipalityId))}/${encodeURIComponent(String(statisticalSectorId))}`);
      }
    };

    const handleStatSectorMouseMove = (event) => {
      const sector = event.features?.[0];
      const sectorId = sector?.id;
      if (sectorId === undefined || sectorId === null) return;

      if (hoveredStatSectorId === sectorId) return;
      setFeatureHoverState('stat-sector', 'Stat_sector', hoveredStatSectorId, false);
      setFeatureHoverState('stat-sector', 'Stat_sector', sectorId, true);
      hoveredStatSectorId = sectorId;
    };

    const handleStatSectorMouseLeave = () => {
      setFeatureHoverState('stat-sector', 'Stat_sector', hoveredStatSectorId, false);
      hoveredStatSectorId = undefined;
    };

    const handleGemeenteMouseMove = (event) => {
      const gemeenteId = event.features?.[0]?.id;
      if (gemeenteId !== undefined && gemeenteId !== null) {
        if (hoveredGemeenteId === gemeenteId) return;
        setFeatureHoverState('gemeente', 'Gemeente', hoveredGemeenteId, false);
        setFeatureHoverState('gemeente', 'Gemeente', gemeenteId, true);
        hoveredGemeenteId = gemeenteId;
      }
    };

    const handleGemeenteMouseLeave = () => {
      setFeatureHoverState('gemeente', 'Gemeente', hoveredGemeenteId, false);
      hoveredGemeenteId = undefined;
    };

    const interactiveLayerIds = [layers.gemeente.id, layers.statSector.id];
    const handleInteractiveMouseMove = (event) => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: interactiveLayerIds,
      });
      map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : '';
    };

    map.on('click', layers.gemeente.id, handleGemeenteClick);
    map.on('click', layers.statSector.id, handleStatSectorClick);
    map.on('mousemove', layers.gemeente.id, handleGemeenteMouseMove);
    map.on('mouseleave', layers.gemeente.id, handleGemeenteMouseLeave);
    map.on('mousemove', layers.statSector.id, handleStatSectorMouseMove);
    map.on('mouseleave', layers.statSector.id, handleStatSectorMouseLeave);
    map.on('mousemove', handleInteractiveMouseMove);
    map.on('idle', updateHeatReceivedLegendMode);
    map.on('moveend', updateHeatReceivedLegendMode);

    return () => {
      map.off('click', layers.gemeente.id, handleGemeenteClick);
      map.off('click', layers.statSector.id, handleStatSectorClick);
      map.off('mousemove', layers.gemeente.id, handleGemeenteMouseMove);
      map.off('mouseleave', layers.gemeente.id, handleGemeenteMouseLeave);
      map.off('mousemove', layers.statSector.id, handleStatSectorMouseMove);
      map.off('mouseleave', layers.statSector.id, handleStatSectorMouseLeave);
      map.off('mousemove', handleInteractiveMouseMove);
      map.off('idle', updateHeatReceivedLegendMode);
      map.off('moveend', updateHeatReceivedLegendMode);
      setFeatureHoverState('gemeente', 'Gemeente', hoveredGemeenteId, false);
      setFeatureHoverState('stat-sector', 'Stat_sector', hoveredStatSectorId, false);
      map.getCanvas().style.cursor = '';
      Object.values(layers).forEach(({ id }) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      Object.values(sources).forEach(({ id }) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
  }, [dispatch, map, navigate]);

  useEffect(() => {
    if (!map) return undefined;

    const layerMaximums = {
      [layers.gemeente.id]: heatReceivedMaximums[legendModes.HEAT_RECEIVED_MUNICIPALITY],
      [layers.statSector.id]: heatReceivedMaximums[legendModes.HEAT_RECEIVED_STAT_SECTOR],
    };
    Object.entries(layerMaximums).forEach(([layerId, maximum]) => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(
          layerId,
          'fill-color',
          getFillColorExpression(legendMode, maximum),
        );
      }
    });

    return undefined;
  }, [heatReceivedMaximums, legendMode, map]);

  return <Outlet context={{ map }} />;
}

export default Consumers;
